import { useEffect, useRef, useState } from "react";
import { Flex } from "rebass/styled-components";
import styled from "styled-components";

import { LoadingSpinner } from "MVVM/Components/Lottie";
import { Top5WidgetComments } from "MVVM/Components/Top5WidgetV2/Top5WidgetComments";
import { EmptyList } from "MVVM/Views/ClipExperience/EmptyList";
import { ClipCommentsViewModel } from "./ViewModel";
import { ClipComment } from "./ClipComment";
import { IClipComment } from "./@types";
import { ClipCommentForm } from "./ClipCommentForm";

interface IClipCommentsProps {
  clipId: string;
  disabled?: boolean;
  setCommentCount?: (arg0: number) => void;
  scrollToBottom?: boolean;
  maxHeight?: string;
  variant?: string;
}

const ClipComments = ({
  clipId,
  disabled,
  setCommentCount,
  scrollToBottom = false,
  maxHeight,
  variant = "standard",
}: IClipCommentsProps) => {
  const { data, error, loading, loadMoreComments, hasNextPage, isLoadingMore } =
    ClipCommentsViewModel({ clipId });

  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = internalRef;
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollPositionRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const lastEndCursorRef = useRef<string | null>(null);
  const previousEndCursorRef = useRef<string | null>(null);
  const previousCommentCountRef = useRef<number>(0);

  //Scroll to bottom on initial load of comments.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !data || hasScrolledToBottom) return;

    const comments = data?.clip?.comments?.nodes;
    if (comments && comments.length > 0) {
      container.scrollTop = container.scrollHeight;

      setHasScrolledToBottom(true);
    }
  }, [data, containerRef, hasScrolledToBottom]);

  //Scroll to bottom if parent tells us to.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !data || !scrollToBottom) return;

    const comments = data?.clip?.comments?.nodes;
    if (comments && comments.length > 0) {
      container.scrollTop = container.scrollHeight;
    }
  }, [scrollToBottom, data, containerRef]);

  // Auto-scroll to bottom when a new comment is added (not when paginating)
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !data) return;

    const comments = data?.clip?.comments?.nodes;
    if (!comments || comments.length === 0) return;

    const currentEndCursor = data.clip.comments.pageInfo.endCursor;
    const currentCommentCount = comments.length;
    const previousEndCursor = previousEndCursorRef.current;
    const previousCommentCount = previousCommentCountRef.current;

    const isNewComment =
      currentEndCursor === previousEndCursor &&
      currentCommentCount > previousCommentCount;

    if (isNewComment) {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }

    previousEndCursorRef.current = currentEndCursor;
    previousCommentCountRef.current = currentCommentCount;
  }, [data, containerRef]);

  //Maintain scroll position when more comments get loaded.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    if (isLoadingMore && !scrollPositionRef.current) {
      scrollPositionRef.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      };
    } else if (!isLoadingMore && scrollPositionRef.current) {
      const { scrollHeight: prevScrollHeight, scrollTop: prevScrollTop } =
        scrollPositionRef.current;
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - prevScrollHeight;
      container.scrollTop = prevScrollTop + heightDifference;
      scrollPositionRef.current = null;
    }
  }, [isLoadingMore, containerRef]);

  //Infinite Pagination.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop } = container;
      const isAtTop = scrollTop <= 50;

      if (isAtTop && hasNextPage && !isLoadingMore) {
        const currentEndCursor = data?.clip?.comments?.pageInfo?.endCursor;

        if (currentEndCursor && currentEndCursor !== lastEndCursorRef.current) {
          lastEndCursorRef.current = currentEndCursor;
          loadMoreComments();
        }
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, data, hasNextPage, isLoadingMore, loadMoreComments]);

  if (loading)
    return (
      <Flex justifyContent="center" alignItems="center" mt={80}>
        <LoadingSpinner height={20} width={20} />
      </Flex>
    );
  if (!data || error) return <></>;

  const comments = data?.clip?.comments?.nodes;

  setCommentCount &&
    setCommentCount(data?.clip?.comments?.pageInfo?.totalCount || 0);

  if (variant === "top5Widget")
    return (
      <Top5WidgetComments
        ref={internalRef}
        disabled={disabled || false}
        comments={comments}
        isLoadingMore={isLoadingMore}
        clipId={clipId}
      />
    );

  return (
    <>
      <ClipCommentsWrapper>
        <ClipCommentsContainer ref={internalRef} maxHeight={maxHeight}>
          {disabled ? (
            <EmptyList text="Comments are disabled" />
          ) : comments?.length ? (
            <>
              {isLoadingMore && (
                <LoadingContainer>
                  <LoadingSpinner height={20} width={20} />
                </LoadingContainer>
              )}
              {comments.map((comment: IClipComment) => (
                <ClipComment key={comment.id} comment={comment} />
              ))}
            </>
          ) : (
            <EmptyList text="No comments posted yet" />
          )}
        </ClipCommentsContainer>
      </ClipCommentsWrapper>
      {!disabled && <ClipCommentForm clipId={clipId} />}
    </>
  );
};

const ClipCommentsWrapper = styled(Flex)<{ maxHeight?: string }>`
  flex-direction: column;
  height: 100%;
  min-height: 0;
  ${(props) => props.maxHeight && `max-height: ${props.maxHeight};`};
  overflow: hidden;
  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-direction: column-reverse;
  }
`;

const ClipCommentsContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: none;
  width: 100%;
  ${(props) => props.maxHeight && `max-height: ${props.maxHeight};`};

  ::-webkit-scrollbar {
    display: none;
  }
`;

const LoadingContainer = styled(Flex)`
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px 0;
`;

export { ClipComments };
